import React from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { formatNumber } from '../../utils/helpers';
import ReportFilters from './ReportFilters';
import useProductAdminReports from './useProductAdminReports';
import styles from './ProductAdmin.module.css';
import ui from './ProductAdminDashboard.module.css';
import t from './AdminTable.module.css';
import { FileCheckIcon, PenIcon, ActivityIcon, ChartIcon } from './ProductAdminIcons';
import { SectionHeading, StatTile } from './AdminWidgets';

// Signing activity for the selected period, in total and per organization
export default function ProductAdminUsage() {
  const navigate = useNavigate();
  const {
    filters, setFilters, companies, usage, loading, error, load,
  } = useProductAdminReports({ period: 'month', withUsage: true });

  // Busiest organizations first; organizations with no activity are left out
  const active = companies
    .filter((item) => item.usage.events || item.usage.signed_files || item.usage.signature_count)
    .sort((a, b) => (b.usage.signed_files - a.usage.signed_files) || (b.usage.signature_count - a.usage.signature_count));

  return (
    <div className={ui.container}>
      <header className={ui.pageHeader}>
        <h1 className={ui.title}>Usage &amp; Activity</h1>
        <p className={ui.subtitle}>Documents signed, signatures and signing runs across all organizations.</p>
      </header>

      <ReportFilters filters={filters} setFilters={setFilters} onApply={load} loading={loading} />

      {error && <div className={styles.error}>{error}</div>}

      <section className={`${ui.statsGrid} ${ui.statsThree}`}>
        <StatTile tone="green" title="Documents signed" value={formatNumber(usage?.files)}
          hint="In the selected period" icon={<FileCheckIcon />} />
        <StatTile tone="amber" title="Signatures" value={formatNumber(usage?.signature_count)}
          hint="In the selected period" icon={<PenIcon />} />
        <StatTile tone="purple" title="Signing runs" value={formatNumber(usage?.events)}
          hint="In the selected period" icon={<ActivityIcon />} />
      </section>

      <section className={ui.panel}>
        <SectionHeading
          tone="purple"
          icon={<ChartIcon />}
          title="Activity by Organization"
          text="Organizations that signed documents in the selected period, busiest first."
        />
        {loading ? <p className={styles.loading}>Loading…</p> : (
          <div className={t.wrap}>
            <table className={t.table}>
              <thead>
                <tr>
                  <th>Organization</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideSm}`}>Signing runs</th>
                  <th className={`${t.wStat} ${t.num}`}>Documents signed</th>
                  <th className={`${t.wStat} ${t.num} ${t.hideXs}`}>Signatures</th>
                  <th className={t.action} />
                </tr>
              </thead>
              <tbody>
                {active.map(item => (
                  <tr key={item.admins[0]?.id || item.company.company_name}>
                    <td className={t.name} title={item.company.company_name}>{item.company.company_name}</td>
                    <td className={`${t.num} ${t.hideSm}`} title={formatNumber(item.usage.events)}>{formatNumber(item.usage.events)}</td>
                    <td className={t.num} title={formatNumber(item.usage.signed_files)}>{formatNumber(item.usage.signed_files)}</td>
                    <td className={`${t.num} ${t.hideXs}`} title={formatNumber(item.usage.signature_count)}>{formatNumber(item.usage.signature_count)}</td>
                    <td className={t.action}>
                      <Button size="sm" variant="secondary" className={t.softButton} onClick={() => navigate(`/product-admin/company/${item.admins[0]?.id}`)}>
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!active.length && (
              <div className={styles.emptyState}>
                <strong>No signing activity</strong>
                <span>Nothing was signed in the selected period.</span>
              </div>
            )}
          </div>
        )}
      </section>
    </div>
  );
}
